import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import ExperienceTilePrices from 'components/Experiences/ExperienceTiles/ExperienceTilePrices'
import Divider from 'LuxKit/Divider'
import GeoContext from 'contexts/geoContext'
import { CurrencyProvider } from 'contexts/currencyContext'
import ExperienceOfferPageTicketSelection from 'components/Experiences/ExperienceOfferPage/ExperienceOfferPageTicketSelection'
import ExperienceOfferPageStateProvider from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateProvider'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import useTripExperiencePageState, { type TripExperienceInitialState } from '../../hooks/useTripExperiencePageState'

export interface TripExperienceSelection {
  date: string | undefined
  time: string | undefined
  redemptionLocationId: string | undefined
  pickupPointId: string | undefined
  tickets: Array<{ ticketId: string; count: number }>
}

const PricingAvailabilitySection = styled.div`
  scroll-margin-top: ${rem(24)};
`

interface Props {
  price?: number
  memberPrice?: number
  memberDiscountPercent?: number
  value?: number
  currencyCode: string
  deeplinkUrl: string
  isSoldOut: boolean
  standaloneOffer?: App.ExperienceOffer
  initialPageState?: TripExperienceInitialState
  onSaveToTrip?: (selection: TripExperienceSelection) => void
  scrollToPricing?: boolean
}

function ExperienceDetailPricing({
  price,
  memberPrice,
  memberDiscountPercent,
  value,
  currencyCode,
  deeplinkUrl,
  isSoldOut,
  standaloneOffer,
  initialPageState,
  onSaveToTrip,
  scrollToPricing,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const pricingSectionRef = useRef<HTMLDivElement>(null)
  const href = `/${geo.currentRegionCode}${deeplinkUrl}`

  const hasDiscount = value !== undefined && price !== undefined && value > price
  const discountPercent = hasDiscount ? Math.round(((value - price) / value) * 100) : 0

  const isTransfer = standaloneOffer?.productType === 'transfer'

  const [pageState, pageDispatch] = useTripExperiencePageState(initialPageState, standaloneOffer)
  const isSaveDisabled = Object.values(pageState.ticketCounts).every(c => c === 0)
  const experienceView = useMemo(
    () => (standaloneOffer && !isTransfer) ? mapExperienceToBookingView(standaloneOffer) : undefined,
    [standaloneOffer, isTransfer],
  )

  const handleCheckAvailability = useCallback(() => {
    pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    if (!scrollToPricing) return
    pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [])

  const handleSaveToTrip = useCallback(() => {
    if (!onSaveToTrip) return
    const tickets = Object.entries(pageState.ticketCounts)
      .filter(([, count]) => count > 0)
      .map(([ticketId, count]) => ({ ticketId, count }))
    onSaveToTrip({
      date: pageState.date,
      time: pageState.time,
      redemptionLocationId: pageState.redemptionLocation?.id,
      pickupPointId: pageState.pickupPoint?.id,
      tickets,
    })
  }, [onSaveToTrip, pageState])

  const checkAvailabilityLabel = intl.formatMessage({
    id: 'cz6O9A05fQXwuzafKdDO',
    defaultMessage: 'Check availability',
    description: 'Button label to scroll to pricing and availability section for experience',
  })

  const bookNowLabel = intl.formatMessage({
    id: 'azlPTkuZgo66yhqZs2e4',
    defaultMessage: 'Book now',
    description: 'Button label to book an experience offer on the experience detail drawer',
  })

  const soldOutLabel = intl.formatMessage({
    id: 'r1Z0H_THzeGe1u2EN68c',
    defaultMessage: 'Sold out',
    description: 'Label shown when experience offer is sold out on the experience detail drawer',
  })

  const pricingHeading = intl.formatMessage({
    id: 'u_VwtpzWYhCsiddc0cPX',
    defaultMessage: 'Pricing & availability',
    description: 'Section heading for experience pricing and availability',
  })

  const saveToTripLabel = intl.formatMessage({
    id: 'zVz2FmzA6yPv71xN9YX4',
    defaultMessage: 'Save to trip',
    description: 'Button label to save selected experience ticket choices to the trip planner cart',
  })

  return (
    <CurrencyProvider currency={currencyCode}>
      <Group direction="vertical" gap={24}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <Group direction="vertical" gap={4}>
            {price !== undefined && (
              <ExperienceTilePrices
                totalPrice={price}
                totalMemberPrice={memberPrice}
                totalGrossPrice={value}
                discountPercentage={discountPercent}
                discountMemberPercentage={memberDiscountPercent}
                isFromPrice
                ticketUnitLabel="person"
                size="L"
              />
            )}
          </Group>
          <TextButton
            kind="primary"
            size="medium"
            onClick={handleCheckAvailability}
            disabled={isSoldOut}
          >
            {isSoldOut ? soldOutLabel : checkAvailabilityLabel}
          </TextButton>
        </Group>

        <Divider kind="secondary" />

        <Group direction="vertical" gap={8}>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'pYLZeh0rjjWTE-Ak6xb9',
              defaultMessage: 'Why it fits your trip',
              description: 'Section heading for why the experience fits the user trip',
            })}
          </Heading>
          <BodyText variant="medium" colour="light-foreground-alt">
            {intl.formatMessage({
              id: '-CHvPsdyvAZP0hXoB9hn',
              defaultMessage: "This experience is a great match for your trip — it's conveniently located, highly rated by travellers, and offers a memorable way to explore the destination. Whether you're after culture, adventure, or relaxation, this activity adds something special to your itinerary.",
              description: 'Generic description explaining why the experience fits the user trip',
            })}
          </BodyText>
        </Group>

        <Divider kind="secondary" />

        <PricingAvailabilitySection ref={pricingSectionRef}>
          <Group direction="vertical" gap={16}>
            <Heading variant="heading5">{pricingHeading}</Heading>

            {experienceView && (
              <ExperienceOfferPageStateProvider state={pageState} dispatch={pageDispatch}>
                <ExperienceOfferPageTicketSelection experienceView={experienceView} />
              </ExperienceOfferPageStateProvider>
            )}

            {onSaveToTrip && experienceView && (
              <Group direction="horizontal" horizontalAlign="end">
                <TextButton
                  kind="primary"
                  size="medium"
                  startIcon={<LineCheckCircleIcon />}
                  onClick={handleSaveToTrip}
                  disabled={isSaveDisabled}
                >
                  {saveToTripLabel}
                </TextButton>
              </Group>
            )}

            {!experienceView && !isSoldOut && (
              <Group direction="horizontal" horizontalAlign="end">
                <TextButton
                  kind="primary"
                  size="medium"
                  endIcon={<LineExternalLinkAltIcon />}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {bookNowLabel}
                </TextButton>
              </Group>
            )}
          </Group>
        </PricingAvailabilitySection>
      </Group>
    </CurrencyProvider>
  )
}

export default ExperienceDetailPricing
